import styled from 'styled-components';

import { SKEW_ANGLE } from '@utils/constants';
import { DefaultText } from '@components/text';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  width: 100%;
`;

export const Label = styled(DefaultText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  color: ${({ theme }) => theme.colors.grey[200]};
`;

export const InputContainer = styled.div<{
  $isError?: boolean;
  $isSkewed?: boolean;
  $isOpen?: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  padding: 8px 16px;
  background-color: transparent;
  border: 0.5px solid
    ${({ $isError, $isOpen, theme }) =>
      $isError
        ? theme.colors.red[500]
        : $isOpen
          ? theme.colors.white
          : theme.colors.grey[300]};
  border-radius: ${({ $isSkewed }) => ($isSkewed ? '0' : '4px')};
  transform: ${({ $isSkewed }) =>
    $isSkewed ? `skew(-${SKEW_ANGLE}deg)` : 'none'};
  cursor: pointer;

  &:hover {
    border-color: ${({ $isError, theme }) =>
      $isError ? theme.colors.red[500] : theme.colors.white};
  }
`;

export const StyledInput = styled.input<{ $isSkewed?: boolean }>`
  flex: 1;
  background-color: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fonts.fontSize.base};
  font-weight: 400;
  line-height: 1.2;
  transform: ${({ $isSkewed }) =>
    $isSkewed ? `skew(${SKEW_ANGLE}deg)` : 'none'};
  cursor: pointer;
  pointer-events: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey[300]};
    opacity: 0.7;
    font-size: ${({ theme }) => theme.fonts.fontSize.base};
    font-weight: 400;
    line-height: 1.2;
  }
`;

export const IconContainer = styled.div<{ $isSkewed?: boolean }>`
  display: flex;
  align-items: center;
  transform: ${({ $isSkewed }) =>
    $isSkewed ? `skew(${SKEW_ANGLE}deg)` : 'none'};
  flex-shrink: 0;
`;

export const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.red[500]};
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
  margin-top: 2px;
  float: left;
`;

export const DropdownContainer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 9999;
  background-color: ${({ theme }) => theme.colors.grey[700]};
  border: 1px solid ${({ theme }) => theme.colors.grey[600]};
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  padding: 12px;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  min-width: 280px;
`;

export const TimeInputContainer = styled.div`
  margin-bottom: 4px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const TimeInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const TimeInput = styled.input<{ $isError?: boolean }>`
  width: 100%;
  height: 40px;
  padding: 4px 32px 4px 8px;
  background-color: transparent;
  border: 0.5px solid
    ${({ $isError, theme }) =>
      $isError ? theme.colors.red[500] : theme.colors.grey[300]};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fonts.fontSize.base};
  font-family: ${({ theme }) => theme.fonts.fontFamily.inter};
  font-weight: ${({ theme }) => theme.fonts.fontWeight.regular};
  line-height: 1.2;
  cursor: pointer;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey[300]};
    opacity: 0.7;
    font-size: ${({ theme }) => theme.fonts.fontSize.base};
    font-weight: 400;
    line-height: 1.2;
  }

  &:focus {
    outline: none;
    border-color: ${({ $isError, theme }) =>
      $isError ? theme.colors.red[500] : theme.colors.white};
  }

  &::-webkit-calendar-picker-indicator {
    display: none;
  }
`;

export const TimeIconButton = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grey[300]};
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.white};
  }
`;

export const CustomTimeDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10000;
  background-color: ${({ theme }) => theme.colors.grey[700]};
  border: 1px solid ${({ theme }) => theme.colors.grey[600]};
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  padding: 12px;
  gap: 8px;
`;

export const TimeColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const TimeColumnHeader = styled.div`
  color: ${({ theme }) => theme.colors.grey[300]};
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
  font-weight: 500;
  text-align: center;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const TimeOptionsList = styled.div`
  max-height: 150px;
  overflow-y: auto;
  border-radius: 4px;
  ${({ theme }) => theme.scrollbar.thin}
`;

export const TimeOption = styled.div<{ $isSelected: boolean }>`
  padding: 6px 8px;
  text-align: center;
  color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.white : theme.colors.grey[200]};
  background-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.purple[300] : 'transparent'};
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  font-family: ${({ theme }) => theme.fonts.fontFamily.inter};
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  margin-bottom: 2px;

  &:hover {
    background-color: ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.purple[300] : theme.colors.purple[200]};
    color: ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.white : theme.colors.grey[700]};
  }
`;

export const DayPickerWrapper = styled.div`
  .rdp-root {
    --rdp-accent-color: ${({ theme }) => theme.colors.purple[300]};
    --rdp-accent-background-color: ${({ theme }) => theme.colors.purple[300]};
    --rdp-day-height: 36px;
    --rdp-day-width: 36px;
    --rdp-day_button-height: 34px;
    --rdp-day_button-width: 34px;
    --rdp-day_button-border-radius: 2px;
    --rdp-today-color: ${({ theme }) => theme.colors.purple[300]};
    --rdp-disabled-opacity: 0.5;
    --rdp-outside-opacity: 0.5;
    --rdp-range_middle-background-color: ${({ theme }) =>
      theme.colors.purple[200]};
    --rdp-range_middle-color: ${({ theme }) => theme.colors.grey[700]};
    --rdp-range_start-background: ${({ theme }) => theme.colors.purple[300]};
    --rdp-range_start-color: ${({ theme }) => theme.colors.white};
    --rdp-range_end-background: ${({ theme }) => theme.colors.purple[300]};
    --rdp-range_end-color: ${({ theme }) => theme.colors.white};
    --rdp-weekday-opacity: 0.7;
    color: ${({ theme }) => theme.colors.grey[200]};
    font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  }

  .rdp-month_caption {
    color: ${({ theme }) => theme.colors.white};
    font-weight: 500;
    font-size: ${({ theme }) => theme.fonts.fontSize.base};
    margin-bottom: 8px;
  }

  .rdp-weekday {
    color: ${({ theme }) => theme.colors.grey[300]};
    font-weight: 400;
    font-size: ${({ theme }) => theme.fonts.fontSize.xs};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .rdp-day {
    color: ${({ theme }) => theme.colors.grey[200]};
    font-size: ${({ theme }) => theme.fonts.fontSize.sm};
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .rdp-day:hover:not(.rdp-day_disabled):not(.rdp-day_selected) {
    text-align: -webkit-center;
    background-color: ${({ theme }) => theme.colors.purple[200]};
    color: ${({ theme }) => theme.colors.grey[700]};
  }

  .rdp-day_selected {
    background-color: ${({ theme }) => theme.colors.purple[300]};
    color: ${({ theme }) => theme.colors.white};
    font-weight: 500;
  }

  .rdp-day_today:not(.rdp-day_selected) {
    color: ${({ theme }) => theme.colors.purple[300]};
    font-weight: 500;
    border: 1px solid ${({ theme }) => theme.colors.purple[300]};
  }

  .rdp-day_outside {
    color: ${({ theme }) => theme.colors.grey[400]};
    opacity: 0.5;
  }

  .rdp-day_disabled {
    color: ${({ theme }) => theme.colors.grey[500]};
    opacity: 0.5;
    cursor: not-allowed;
  }

  .rdp-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .rdp-nav_button {
    color: ${({ theme }) => theme.colors.grey[200]};
    background: transparent;
    border: none;
    border-radius: 4px;
    padding: 4px;
    transition: all 0.2s ease;
    cursor: pointer;
  }

  .rdp-nav_button:hover:not([disabled]) {
    background-color: ${({ theme }) => theme.colors.purple[200]};
    color: ${({ theme }) => theme.colors.grey[700]};
  }

  .rdp-nav_button[disabled] {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .rdp-chevron {
    fill: currentColor;
    width: 12px;
    height: 12px;
  }

  .rdp-dropdown {
    background-color: ${({ theme }) => theme.colors.grey[700]};
    border: 1px solid ${({ theme }) => theme.colors.grey[600]};
    border-radius: 4px;
    color: ${({ theme }) => theme.colors.white};
    font-size: ${({ theme }) => theme.fonts.fontSize.sm};
    padding: 4px 8px;
  }

  .rdp-dropdown option {
    background-color: ${({ theme }) => theme.colors.grey[700]};
    color: ${({ theme }) => theme.colors.white};
  }
`;
